import * as React from "react";

import { Button } from "@replo/design-system/components/button/Button";
import { Input } from "@replo/design-system/components/input/Input";
import { Skeleton } from "@replo/design-system/components/skeleton/Skeleton";
import twMerge from "@replo/design-system/utils/twMerge";

import { SkeletonPreviewPlaceholder } from "./SkeletonPreviewPlaceholder";

interface CustomStylesLoadingCardProps {
  isSelected: boolean;
  onSelect: () => void;
  onCancel: () => void;
  pendingUrl: string;
}

export function CustomStylesLoadingCard({
  isSelected,
  onSelect,
  onCancel,
  pendingUrl,
}: CustomStylesLoadingCardProps) {
  const loadingGradient =
    "linear-gradient(279deg, rgba(253,211,126,0.05) 12%, rgba(243,77,114,0.05) 20%, rgba(124,127,186,0.05) 110%)";

  const skeletonGradient =
    "linear-gradient(277deg, rgba(253, 211, 126, 0.10) -11.74%, rgba(243, 77, 114, 0.10) 19.68%, rgba(124, 127, 186, 0.10) 109.58%)";

  return (
    <div
      onClick={onSelect}
      role="button"
      tabIndex={0}
      aria-label="Loading custom styles"
      className={twMerge(
        "border-0.5 border-border flex flex-col gap-0 rounded-xl p-0 shadow-md overflow-hidden cursor-pointer w-full md:w-75 shrink-0 h-[356px] transition-all duration-200",
        isSelected ? "border-[0.8px] border-black" : "border-transparent",
      )}
      style={{
        background: isSelected
          ? undefined
          : "linear-gradient(white, white) padding-box, var(--color-gradient-ai-40) border-box",
        borderWidth: isSelected ? undefined : "1px",
        borderStyle: "solid",
      }}
    >
      <div
        className="flex flex-col gap-0 p-0 flex-1 overflow-hidden relative"
        style={{
          background: loadingGradient,
        }}
      >
        <SkeletonPreviewPlaceholder />

        <div className="absolute top-4 left-3 bg-white border-0.5 border-border p-3 rounded-xl shadow-2xs">
          <div
            className="w-48 h-10 rounded-xl"
            style={{ background: skeletonGradient }}
          >
            <Skeleton className="w-48 h-10 rounded-xl" />
          </div>
        </div>

        <div className="absolute top-24 -right-6 bg-white border-0.5 border-border p-3 rounded-lg shadow-2xs">
          <div className="flex gap-4">
            <div className="flex flex-col gap-1">
              <span className="text-2xs font-medium text-placeholder">
                Headings
              </span>
              <div
                className="w-24 h-3.5 rounded-xl"
                style={{ background: skeletonGradient }}
              >
                <Skeleton className="w-24 h-3.5 rounded-xl" />
              </div>
            </div>
            <div className="flex flex-col gap-1 w-23">
              <span className="text-2xs font-medium text-placeholder">
                Body
              </span>
              <div
                className="w-24 h-3.5 rounded-xl"
                style={{ background: skeletonGradient }}
              >
                <Skeleton className="w-24 h-3.5 rounded-xl" />
              </div>
            </div>
          </div>
        </div>

        <div className="absolute top-44 left-3 bg-white border-0.5 border-border p-3 rounded-xl shadow-2xs">
          <div
            className="w-17 h-5 rounded-xl"
            style={{ background: skeletonGradient }}
          >
            <Skeleton className="w-17 h-5 rounded-xl" />
          </div>
        </div>

        <div className="absolute bottom-0 inset-x-0 h-16 bg-gradient-to-t from-white to-transparent pointer-events-none" />
      </div>

      <div className="flex flex-col items-start gap-3 p-4 bg-white relative z-10">
        <div className="flex flex-col gap-0.5">
          <span className="text-base font-semibold text-default">
            Pulling in your styles
            <span className="inline-flex">
              <span className="animate-typing-dot-bounce duration-2500">.</span>
              <span className="animate-typing-dot-bounce duration-2500 [animation-delay:125ms]">
                .
              </span>
              <span className="animate-typing-dot-bounce duration-2500 [animation-delay:250ms]">
                .
              </span>
            </span>
          </span>
          <span className="text-sm text-muted">
            This should take 10-20 seconds.
          </span>
        </div>
        <div className="flex items-center gap-3 w-full">
          <Input value={pendingUrl} disabled className="flex-1" size="sm" />
          <Button
            variant="secondary"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onCancel();
            }}
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}
